import {
  AddIcon,
  ChevronRightIcon,
  InfoIcon,
  InfoOutlineIcon,
} from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  Button,
  chakra,
  HStack,
  Icon,
  IconButton,
  Image,
  InputGroup,
  InputRightAddon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text as ChakraText,
  Text,
  Tooltip,
  useDisclosure,
  ButtonGroup,
  VStack,
  Checkbox,
  Divider,
  Image as ChakraImage,
  Portal,
  useClipboard,
  Link as ChakraLink,
} from '@chakra-ui/react'
import {
  Menu,
  MenuItem,
  MenuButton,
  MenuDivider,
  SubMenu,
} from '@szhsin/react-menu'
import type { MenuInstance } from '@szhsin/react-menu'
import { Base64 } from 'js-base64'
import _ from 'lodash'
import styled from 'styled-components'
import { ReactElement, useCallback } from 'react'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { IconType } from 'react-icons'
import {
  AiFillHome,
  AiOutlineUnorderedList,
  AiOutlineUpload,
} from 'react-icons/ai'
import {
  BsSortDownAlt,
  BsSortUpAlt,
  BsThreeDots,
  BsThreeDotsVertical,
} from 'react-icons/bs'
import { IoClose, IoGrid, IoSettingsOutline } from 'react-icons/io5'
import { HiSortAscending, HiSortDescending } from 'react-icons/hi'
import {
  MdCollections,
  MdOutlineRefresh,
  MdOutlineShare,
  MdWidgets,
} from 'react-icons/md'
import { IoMdClose } from 'react-icons/io'
import { RiFolderFill } from 'react-icons/ri'
import { useRouteMatch } from 'react-router'
import { Link, useLocation } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import { fileTypeOptionsMapping } from '../../../utils/mediaLibrary'
import { isRestrictedUser } from '../../../utils/roles'
import { normalizeSearchUrlForComparison } from '../../../utils/search'
import { CustomMetadataContext } from '../../providers/CustomMetadataProvider'
import {
  ACLPermissionEnum,
  FileType,
  MLSortDirection,
  MLSortParams,
  MLViewType,
} from '../../constants'
import { useGetQueryParams } from '../../hooks/useGetQueryParams'
import useToast from '../../hooks/useToast'
import type {
  ListSavedSearchSuccessResponse,
  MediaLibrarySearchCollectionResponse,
  ViewableParentSuccessResponse,
} from '../../interfaces'
import type {
  MediaLibrarySearchFolderResponse,
  MediaLibraryModalsControllers,
} from '../../interfaces/index'
import { MLContext, NavBarContext, UserContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'
import CreateMediaCollection from '../MediaCollections/CreateMediaCollection'
import PermissionGuard from '../PermissionGuard'
import StickyShadow from '../StickyShadow'
import { ShareAssetModal, ShareMediaCollectionModal } from './MediaOperations'
import NewButton from './NewButton'
import { FeaturesEnum } from '../../../utils/features'
import { addLeadingSlash, addTrailingSlash } from '../../../utils/urlHelpers'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import { HiOutlineBookmark } from 'react-icons/hi'
import SaveSearchModal from '../SavedSearch/SaveSearchModal'
import ManageSavedSearchModal from '../SavedSearch/ManageSavedSearchModal'
import { HiMiniBookmark } from 'react-icons/hi2'
import { IoIosArrowRoundBack } from 'react-icons/io'
import { useMLWidgetContext } from '../../contexts/MLWidgetContext'
import PreviewToggleButton from './PreviewToggleButton'
import MenuItemWithTooltip from './ContextMenu/MenuItem'
import { useContextMenuItems } from './hooks/useContextMenuItems'

interface Props {
  resetSearch(): void
  controllers: MediaLibraryModalsControllers
}
interface Crumb {
  label: string
  href: string
  icon: IconType
  folderPath?: string
}

// const SaveSearchButton = ({ onClick }: { onClick(): void }) => {
//   const { t } = useTranslation()
//   return (
//     <PermissionGuard
//       permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
//     >
//       {(hasPermission: boolean) =>
//         hasPermission ? (
//           <Button
//             variant="link"
//             color="appClearBlue"
//             leftIcon={<HiOutlineBookmark size="16px" />}
//             iconSpacing="4px"
//             onClick={onClick}
//             style={{ marginLeft: '16px' }}
//           >
//             {t('save_this_search', 'Save this search')}
//           </Button>
//         ) : (
//           <></>
//         )
//       }
//     </PermissionGuard>
//   )
// }

const SavedSearchBreadcrumb = ({
  savedSearch,
  getAndSetSavedSearches,
}: {
  savedSearch: ListSavedSearchSuccessResponse
  getAndSetSavedSearches: () => void
}) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { cmFields: customMetadataFields } = useContext(CustomMetadataContext)
  const {
    isOpen: isOpenManageSavedSearchModal,
    onOpen: onOpenManageSavedSearchModal,
    onClose: onCloseManageSavedSearchModal,
  } = useDisclosure()

  const truncatedName =
    savedSearch.name && savedSearch.name.length > 250
      ? savedSearch.name.slice(0, 250) + '...'
      : savedSearch.name

  return (
    <>
      <HStack spacing="4px" maxW="500px">
        <Icon as={HiMiniBookmark} boxSize="16px" color={colors.appSlate} />
        <Tooltip label={truncatedName} hasArrow>
          <Text fontWeight="500" color={colors.appSlate} isTruncated>
            {savedSearch.name}
          </Text>
        </Tooltip>
        <PermissionGuard
          permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
        >
          {(hasPermission: boolean) => {
            const shouldShow =
              !isRestrictedUser(user?.roles) ||
              user?.id === savedSearch.createdBy.userId
            return hasPermission && shouldShow ? (
              <Tooltip
                label={t('manage_saved_search', 'Manage saved search')}
                hasArrow
              >
                <IconButton
                  aria-label={t('manage_saved_search', 'Manage saved search')}
                  icon={<IoSettingsOutline />}
                  variant="link"
                  minWidth="16px"
                  borderRadius="4px"
                  height="16px"
                  color="appSlate"
                  ml="4px"
                  onClick={e => {
                    e.stopPropagation()
                    onOpenManageSavedSearchModal()
                  }}
                />
              </Tooltip>
            ) : (
              <></>
            )
          }}
        </PermissionGuard>
      </HStack>
      {isOpenManageSavedSearchModal && (
        <ManageSavedSearchModal
          name={savedSearch.name}
          url={savedSearch.url}
          searchId={savedSearch.searchId}
          isPrivate={savedSearch.isPrivate}
          createdBy={savedSearch.createdBy}
          isOpen={isOpenManageSavedSearchModal}
          onClose={onCloseManageSavedSearchModal}
          getAndSetSavedSearches={getAndSetSavedSearches}
        />
      )}
    </>
  )
}

const HeaderToolbar = ({ resetSearch, controllers }: Props): ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isPublicLink, urlPrefix, clientInfo } = useContext(
    PublicLinksInfoContext,
  )
  const { savedSearches, setSavedSearches } = useContext(NavBarContext)
  const [state, dispatch] = useContext(MLContext)
  const { user } = useContext(UserContext)
  const { cmFields: customMetadataFields } = useContext(CustomMetadataContext)
  const clientNumber = user?.clientNumber ?? clientInfo?.clientNumber
  const location = useLocation()
  const [mlWidgetInsertButtonDisable, setMlWidgetInsertButtonDisable] =
    useState<boolean>(false)
  const [
    mlWidgetInsertButtonDisableTooltip,
    setMlWidgetInsertButtonDisableTooltip,
  ] = useState<any>(null)
  const [mlWidgetInsertButtonHidden, setMlWidgetInsertButtonHidden] =
    useState<boolean>(false)

  const {
    folderPath,
    currentMediaCollection,
    sortDescriptors: { sortParam, sortDirection },
    view,
    loadingMediaCollections,
    viewableParent,
    loadingViewableParent,
    mediaCollectionRoute,
    selectedFolders,
    selectedFiles,
    fileList,
    folderList,
    mediaCollectionList,
    selectedMediaCollections,
    selectedBreadcrumbFolder,
    selectedBreadcrumbMediaCollection,
    folderPermission,
    refetch,
    refetchFiles,
    refetchFolders,
    refetchMediaCollections,
    refetchDrawerFolders,
    refetchViewableParent,
    refetchFolderPermission,
  } = state

  const {
    url,
    params: { folderPathBase64 },
  } = useRouteMatch<{ folderPathBase64?: string }>()

  const queryFromUrl = useGetQueryParams()

  const [crumbs, setCrumbs] = useState<Crumb[]>([])

  const [isBlur, setIsBlur] = useState<boolean>(false)

  const [shareableAsset, setShareableAsset] = useState<string>('')

  const [shareFolderResponse, setShareFolderResponse] =
    useState<MediaLibrarySearchFolderResponse>()

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const refMap = {
    organize: useRef<MenuInstance>(null),
    edit: useRef<MenuInstance>(null),
    tags: useRef<MenuInstance>(null),
    download: useRef<MenuInstance>(null),
    share: useRef<MenuInstance>(null),
  }
  const contextMenuItemList = useContextMenuItems({
    setCopyMoveOperation: controllers.copyMoveState[1],
    setTagOperation: controllers.tagState[1],
    onOpenCopyMoveDrawer: controllers.copyMoveDrawer.onOpen,
    onOpenRenameFileModal: controllers.renameFile.onOpen,
    onOpenRenameFolderModal: controllers.renameFolder.onOpen,
    onOpenRenameMediaCollectionModal: controllers.renameMediaCollection.onOpen,
    onOpenPublishFilesModal: controllers.publishFiles.onOpen,
    onOpenTagsModal: controllers.tags.onOpen,
    onOpenAutoTagsModal: controllers.autoTags.onOpen,
    onOpenRemoveAssetFromMultipleMediaCollections:
      controllers.removeAssetFromMultipleMediaCollections.onOpen,
    onOpenRemoveMultipleAssetsFromMediaCollection:
      controllers.removeMultipleAssetsFromMediaCollection.onOpen,
    onOpenDeleteMediaModal: controllers.deleteMedia.onOpen,
    onOpenDeleteFileVersionModal: controllers.deleteFileVersion.onOpen,
    onOpenRestoreFileVersionModal: controllers.restoreFileVersion.onOpen,
    onOpenRemoveBgModal: controllers.removeBg.onOpen,
    onOpenCustomFocusAreaModal: controllers.customFocusArea.onOpen,
    onOpenShareAssetModal: controllers.shareAsset.onOpen,
    onOpenShareMediaCollectionModal: controllers.shareMediaCollection.onOpen,
    onOpenPublicLinksModal: controllers.publicLinks.onOpen,
    onOpenAddToMediaCollection: controllers.addToMediaCollection.onOpen,
    onOpenDownloadModal: controllers.download.onOpen,
    handleEmbedModal: controllers.embedModal[1],
    onOpenApplyPathPolicyModal: controllers.applyPathPolicy.onOpen,
    onOpenApplySavedExtensionModal: controllers.applySavedExtension.onOpen,
    onOpenImageEditor: controllers.imageEditor.onOpen,
    onOpenPhotoEditor: controllers.photoEditor.onOpen,
    onOpenBulkEditCustomMetadataModal:
      controllers.bulkEditCustomMetadata.onOpen,
    refMap,
  })

  const initialFocusRef = useRef<HTMLDivElement>(null)


  useEffect(() => {
    if (!loadingViewableParent) {
      let pathBase: string
      if (currentMediaCollection) {
        pathBase = url.replace(
          folderPathBase64
            ? `/${currentMediaCollection}/${folderPathBase64}`
            : new RegExp(`/${currentMediaCollection}/?`, 'u'),
          '',
        )
      } else {
        pathBase = url.replace(`/${folderPathBase64}`, '')
      }
      let viewableFolderPath = folderPath
      let unViewableBasePath = ''
      if (
        viewableParent &&
        !_.isUndefined((viewableParent as ViewableParentSuccessResponse).asset)
      ) {
        const index = (
          viewableParent as ViewableParentSuccessResponse
        ).asset?.assetPath.lastIndexOf('/')
        if (index) {
          viewableFolderPath = folderPath.slice(
            index,
            viewableFolderPath.length,
          )
          unViewableBasePath = folderPath.slice(0, index)
        }
      }
      const [, ...pathComponents] =
        viewableFolderPath !== '/' ? viewableFolderPath.split('/') : []
      // eslint-disable-next-line prefer-named-capture-group
      if (mediaCollectionRoute) {
        const crumbsArr: Crumb[] = [
          {
            label: 'All collections',
            href: queryFromUrl.includes('view=LIST')
              ? `${pathBase}?view=LIST`
              : pathBase,
            icon: AiFillHome,
          },
        ]
        if (
          currentMediaCollection &&
          (viewableParent as ViewableParentSuccessResponse).mediaCollection
            ?.name
        ) {
          crumbsArr.push(
            {
              label:
                (viewableParent as ViewableParentSuccessResponse)
                  .mediaCollection?.name ?? '',
              href: `${pathBase}/${currentMediaCollection}/${
                queryFromUrl as string
              }`,
              icon: MdCollections,
              folderPath: '/',
            },
            ...pathComponents.map((x, i) => {
              const pathBase64 = Base64.encode(
                `${unViewableBasePath}/${pathComponents
                  .slice(0, i + 1)
                  .join('/')}`,
                true,
              )
              return {
                label: x,
                href: `${pathBase}/${currentMediaCollection}/${pathBase64}${
                  queryFromUrl as string
                }`,
                icon: RiFolderFill,
                folderPath: pathBase64,
              }
            }),
          )
        }
        setCrumbs(crumbsArr)
      } else {
        const crumbsArr = [
          {
            label: 'Home',
            href: `${pathBase}${queryFromUrl as string}`,
            icon: AiFillHome,
          },
        ]
        if (
          (viewableParent as ViewableParentSuccessResponse).asset?.assetPath
        ) {
          crumbsArr.push(
            ...pathComponents.map((x, i) => {
              const pathBase64 = Base64.encode(
                `${unViewableBasePath}/${pathComponents
                  .slice(0, i + 1)
                  .join('/')}`,
                true,
              )
              return {
                label: x,
                href: `${pathBase}/${pathBase64}${queryFromUrl as string}`,
                icon: RiFolderFill,
                folderPath: pathBase64,
              }
            }),
          )
        }
        setCrumbs(crumbsArr)
      }
    }
  }, [
    folderPath,
    viewableParent,
    currentMediaCollection,
    loadingViewableParent,
    queryFromUrl,
  ])

  useEffect(() => {
    if (
      selectedBreadcrumbFolder &&
      !selectedFolders
        .map(x => x.folderId)
        .includes(selectedBreadcrumbFolder.folderId)
    ) {
      dispatch({
        type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_FOLDER,
        payload: {
          folder: undefined,
        },
      })
    }
  }, [selectedFolders, selectedBreadcrumbFolder])

  useEffect(() => {
    if (
      selectedBreadcrumbMediaCollection &&
      !selectedMediaCollections
        .map(x => x.mediaCollectionId)
        .includes(selectedBreadcrumbMediaCollection.mediaCollectionId)
    ) {
      dispatch({
        type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION,
        payload: {
          mediaCollection: undefined,
        },
      })
    }
  }, [selectedMediaCollections, selectedBreadcrumbMediaCollection])

  useEffect(() => {
    const blur: boolean = crumbs.slice(1, crumbs.length - 1).filter(data => {
      if (
        data?.folderPath === '/' &&
        selectedBreadcrumbMediaCollection?.name === data.label
      ) {
        return true
      }
      if (!data?.folderPath || !selectedBreadcrumbFolder?.folderPath) {
        return false
      }
      return (
        data?.folderPath ===
        Base64.encode(selectedBreadcrumbFolder.folderPath, true)
      )
    }).length
      ? false
      : true

    setIsBlur(blur)
  }, [selectedBreadcrumbFolder, selectedBreadcrumbMediaCollection, crumbs])

  useEffect(() => {
    if (isBlur) {
      initialFocusRef.current?.focus()
    }
  }, [isBlur, initialFocusRef.current])

  useEffect(() => {
    if (isPublicLink) {
      return
    }
    const lastBreadcrumb = crumbs[crumbs.length - 1]
    if (
      lastBreadcrumb?.folderPath === '/' &&
      mediaCollectionRoute &&
      currentMediaCollection &&
      (viewableParent as ViewableParentSuccessResponse)?.mediaCollection
    ) {
      const mediaCollection = (viewableParent as ViewableParentSuccessResponse)
        .mediaCollection
      if (
        !mediaCollection?.permission ||
        mediaCollection.permission === ACLPermissionEnum.MANAGE
      ) {
        setShareableAsset('COLLECTION')
        return
      }
    } else if (
      lastBreadcrumb?.folderPath &&
      lastBreadcrumb.folderPath !== '/' &&
      user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      if (
        !(
          isRestrictedUser(user?.roles) &&
          folderPermission !== ACLPermissionEnum.MANAGE
        )
      ) {
        setShareableAsset('FOLDER')
        return
      }
    }

    setShareableAsset('')
  }, [
    crumbs,
    mediaCollectionRoute,
    currentMediaCollection,
    viewableParent,
    folderPermission,
    user,
    isPublicLink,
  ])

  const { settings } = useMLWidgetContext()

  useEffect(() => {
    if (settings?.multiple === false) {
      setMlWidgetInsertButtonDisable(state.selectedFiles.length > 1)
      if (state.selectedFiles.length > 1) {
        setMlWidgetInsertButtonDisableTooltip(
          t('only_one_file_allowed', 'Multiple file selection is not allowed'),
        )
      }
    } else if (settings?.maxFiles && settings.maxFiles > 0) {
      setMlWidgetInsertButtonDisable(
        state.selectedFiles.length > settings.maxFiles,
      )
      const maxFiles = settings.maxFiles
      setMlWidgetInsertButtonDisableTooltip(
        <Trans t={t} i18nKey="max_files_allowed">
          Maximum {{ maxFiles }} files allowed to be selected
        </Trans>,
      )
    }

    // Toolbar settings for widget
    if (
      settings?.toolbar &&
      typeof settings.toolbar.showInsertButton !== 'undefined'
    ) {
      setMlWidgetInsertButtonHidden(!settings.toolbar.showInsertButton)
    }
  }, [state.selectedFiles, settings])

  const shareFolderFunction = useCallback(
    async event => {
      if (isPublicLink) {
        return
      }
      const folder = (await request.get(
        `/api/clients/${
          user?.clientNumber
        }/access-control/breadcrumbs?folderPathBase64=${Base64.encode(
          folderPath,
          true,
        )}`,
      )) as MediaLibrarySearchFolderResponse
      if (request.error) {
        toast({
          description: t(
            'something_went_wrong',
            'Something went wrong, please try again after sometime',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }

      setShareFolderResponse(folder)
      dispatch([
        {
          type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_FOLDER,
          payload: {
            folder,
          },
        },
        {
          type: MediaLibraryActions.SELECT_FOLDER,
          payload: {
            folder,
            event,
            isBreadcrumb: true,
          },
        },
        {
          type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
        },
      ])
      onOpenShareAssetModal()
    },
    [folderPath, user, isPublicLink],
  )

  const getAndSetSavedSearches = async () => {
    const res = (await request.get(
      `/api/media-library/${user?.clientNumber}/saved-searches`,
    )) as ListSavedSearchSuccessResponse[]

    if (request.error) {
      toast({
        description: t(
          'error_in_getting_saved_searches',
          'Error in getting saved searches, please try again later.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      setSavedSearches(res)
    }
  }

  useEffect(() => {
    if (isPublicLink) {
      return
    }
    getAndSetSavedSearches()
  }, [])

  const {
    isOpen: isOpenCreateMediaCollectionModal,
    onOpen: onOpenCreateMediaCollectionModal,
    onClose: onCloseCreateMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShareAssetModal,
    onOpen: onOpenShareAssetModal,
    onClose: onCloseShareAssetModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShareMediaCollectionModal,
    onOpen: onOpenShareMediaCollectionModal,
    onClose: onCloseShareMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenSaveSearchModal,
    onOpen: onOpenSaveSearchModal,
    onClose: onCloseSaveSearchModal,
  } = useDisclosure()

  const VerticalDivider = styled.div`
    width: 1px;
    height: 20px;
    flex-grow: 0;
    border-left: 1px solid #e1e4eb;
  `

  const BreadcrumbReplacement = useMemo<React.ReactElement | null>(() => {
    const FolderPathTooltip = React.memo(
      ({ path }: { path: string }): React.ReactElement => (
        <Tooltip label={path} hasArrow>
          {window.isMediaLibraryWidget === true ? (
            <ChakraLink
              as={Link}
              to={`/media-library/${Base64.encode(state.folderPath, true)}`}
              display="inline-block"
              maxWidth="15ch"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
              verticalAlign="bottom"
              _focus={{ outline: 'none', boxShadow: 'none' }}
            >
              {path.split('/').pop() ?? ''}
            </ChakraLink>
          ) : (
            <ChakraText
              as={ChakraLink}
              isExternal
              href={`/dashboard/media-library/${Base64.encode(
                state.folderPath,
                true,
              )}`}
              display="inline-block"
              maxWidth="15ch"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
              verticalAlign="bottom"
              _focus={{ outline: 'none', boxShadow: 'none' }}
            >
              {path.split('/').pop() ?? ''}
            </ChakraText>
          )}
        </Tooltip>
      ),
    )
    if (!(url === '/search')) {
      return null
    }

    const currentUrl = window.isMediaLibraryWidget
      ? document
          .querySelector('iframe[title="ImageKit Embedded Media Library"]')
          ?.getAttribute('href') || window.location.href
      : window.location.href

    const normalizedCurrentQuery = normalizeSearchUrlForComparison(
      currentUrl,
      customMetadataFields,
    )

    const matchingSearch = (savedSearches || []).find(search => {
      // Compare normalized query strings
      const normalizedSavedQuery = normalizeSearchUrlForComparison(
        search.url,
        customMetadataFields,
      )
      return normalizedSavedQuery === normalizedCurrentQuery
    })

    if (matchingSearch) {
      return (
        <SavedSearchBreadcrumb
          savedSearch={matchingSearch}
          getAndSetSavedSearches={getAndSetSavedSearches}
        />
      )
    }

    // Eg. `Search results for custom search filters`
    if (state.searchQuery && state.searchQuery.trim().length > 0) {
      return (
        <HStack spacing="0.5em">
          <Trans t={t} i18nKey="custom_search_filters_results">
            <ChakraText color={colors.appSlate} isTruncated>
              <chakra.span>Search results for</chakra.span>{' '}
              <chakra.span fontWeight="medium">
                custom search filters
              </chakra.span>
            </ChakraText>
          </Trans>
        </HStack>
      )
    }

    if (state.fileListError === 'Rate limit for dam-only-free plan exceeded') {
      return (
        <Text>
          You have reached the rate limit for the free plan.
          <br />
          Please{' '}
          <ChakraLink href="/dashboard/upgrade-plan">
            upgrade to a paid plan
          </ChakraLink>{' '}
          to continue using this feature.
        </Text>
      )
    }

    if (state.q && (state.searchMode === 'ai-text' || state.searchMode === 'ai-image')) {
      if (state.searchMode === 'ai-image' && state.fileListError) {
        return (
          <HStack spacing="0.5em" maxWidth="100%" minWidth={0} borderRadius="2xl" backgroundColor="appVeryLightPink" padding="4px 8px">
            <Icon as={InfoIcon} color="appWatermelon" boxSize="16px" flexShrink={0} />
            <ChakraText
              color="appWatermelon"
              isTruncated
              maxWidth="100%"
              minWidth={0}
              fontSize="12px"
            >
              {state.fileListError}
            </ChakraText>
          </HStack>
        )
      }
      // return Search results
      return (
        <HStack spacing="0.5em">
          <Trans t={t} i18nKey="fall_back_search_results">
            <ChakraText color={colors.appSlate}>
              <chakra.span>Search results</chakra.span>
            </ChakraText>
          </Trans>
        </HStack>
      )
    }

    // Eg. `Search results for "query text" in Videos`
    if (state.fileType !== '' && state.q) {
      return (
        <HStack spacing="0.5em" maxWidth="100%" minWidth={0} overflow="hidden">
          <Trans t={t} i18nKey="query_text_file_type_search_results">
            <ChakraText color={colors.appSlate}>
              <chakra.span>Search results for</chakra.span>{' '}
              <chakra.span
                fontWeight="medium"
                display="inline-block"
                maxWidth="200px"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                verticalAlign="bottom"
              >
                {{ q: state.q }}
              </chakra.span>
              {{
                fileVersionState: state.isVersionIncludedInSearch
                  ? ' in File versions (including files)'
                  : null,
              }}
              {' '}
              <chakra.span fontWeight="medium">
                {{ fileType: fileTypeOptionsMapping[state.fileType] }}
              </chakra.span>
            </ChakraText>
          </Trans>
        </HStack>
      )
    }

    // Eg. `Search results for "query text"`
    if (state.q) {
      return (
        <HStack spacing="0.5em" maxWidth="100%" minWidth={0} overflow="hidden">
          <Trans t={t} i18nKey="query_text_search_results">
            <ChakraText color={colors.appSlate}>
              <chakra.span>Search results for</chakra.span>{' '}
              <chakra.span
                fontWeight="medium"
                display="inline-block"
                maxWidth="300px"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                verticalAlign="bottom"
              >
                {{ q: state.q }}
              </chakra.span>
              {{
                fileVersionState: state.isVersionIncludedInSearch
                  ? ' in File versions (including files)'
                  : null,
              }}
            </ChakraText>
          </Trans>
        </HStack>
      )
    } else if (state.fileType !== '') {
      // Eg. `Search results for Videos`
      return (
        <HStack spacing="0.5em">
          <Trans t={t} i18nKey="file_type_search_results">
            <ChakraText color={colors.appSlate}>
              <chakra.span>Search results for</chakra.span>{' '}
              <chakra.span fontWeight="medium">
                {{ fileTypeOption: fileTypeOptionsMapping[state.fileType] }}
              </chakra.span>
              {{
                fileVersionState: state.isVersionIncludedInSearch
                  ? ' in File versions (including files)'
                  : null,
              }}
            </ChakraText>
          </Trans>
        </HStack>
      )
    } else if (state.isVersionIncludedInSearch) {
      return (
        <HStack spacing="0.5em">
          <Trans t={t} i18nKey="file_version_search_results">
            <ChakraText color={colors.appSlate}>
              <chakra.span>Search results for</chakra.span>{' '}
              <chakra.span fontWeight="medium">
                File versions (including files)
              </chakra.span>
            </ChakraText>
          </Trans>
        </HStack>
      )
    }

    return null
  }, [state, savedSearches])

  const handleClick: React.MouseEventHandler<HTMLElement> = event => {
    if (event.detail > 1) {
      return
    }
    void state.abortFilesRequest()
    void state.abortFoldersRequest()
  }

  const handleDoubleClick: React.MouseEventHandler<HTMLElement> = event => {
    event.preventDefault()
  }

  const handleContextMenuEvent = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    x: Crumb,
  ): Promise<void> => {
    event.preventDefault()

    if (
      x.folderPath === '/' &&
      mediaCollectionRoute &&
      currentMediaCollection &&
      (viewableParent as ViewableParentSuccessResponse)?.mediaCollection
    ) {
      dispatch([
        {
          type: MediaLibraryActions.SELECT_MEDIA_COLLECTION,
          payload: {
            mediaCollection: (viewableParent as ViewableParentSuccessResponse)
              .mediaCollection as MediaLibrarySearchCollectionResponse,
            event,
          },
        },
        {
          type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION,
          payload: {
            mediaCollection: (viewableParent as ViewableParentSuccessResponse)
              .mediaCollection as MediaLibrarySearchCollectionResponse,
          },
        },
        {
          type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
        },
        {
          type: MediaLibraryActions.OPEN_CONTEXT_MENU,
          payload: { x: event.pageX, y: event.pageY },
        },
      ])
    } else if (x?.folderPath && x.folderPath !== '/') {
      const folder = (await request.get(
        `${urlPrefix}api/clients/${clientNumber}/access-control/breadcrumbs?folderPathBase64=${x.folderPath}`, // its already base64 encoded
      )) as MediaLibrarySearchFolderResponse
      if (request.error) {
        toast({
          description: t(
            'something_went_wrong',
            'Something went wrong, please try again after sometime',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }

      dispatch([
        {
          type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_FOLDER,
          payload: {
            folder,
          },
        },
        {
          type: MediaLibraryActions.SELECT_FOLDER,
          payload: {
            folder,
            event,
            isBreadcrumb: true,
          },
        },
        {
          type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
        },
        {
          type: MediaLibraryActions.OPEN_CONTEXT_MENU,
          payload: { x: event.pageX, y: event.pageY },
        },
      ])
    }
  }

  const hideSortAndOtherOptions = useMemo<boolean>(() => {
    let _hideSortAndOtherOptions = false

    if (isPublicLink) {
      if (mediaCollectionRoute) {
        if (!currentMediaCollection) {
          _hideSortAndOtherOptions = true
        }
      } else if (folderPath === '/') {
        _hideSortAndOtherOptions = true
      }
    }

    return _hideSortAndOtherOptions
  }, [isPublicLink, currentMediaCollection, mediaCollectionRoute, folderPath])

  const showFloatingBar = useMemo<boolean>(() => {
    let _showFloatingBar = false
    if (
      state.selectedFiles.length > 0 ||
      state.selectedFolders.length > 0 ||
      state.selectedMediaCollections.length > 0
    ) {
      _showFloatingBar = true
    }
    return _showFloatingBar
  }, [
    state.selectedFiles,
    state.selectedFolders,
    state.selectedMediaCollections,
  ])

  return (
    <>
      <HStack
        justifyContent="space-between"
        position="sticky"
        top="calc(35px + 24px + 1px)" // main height (header action bar) + padding*2 + borderBottomWidth
        background="white"
        borderBottomWidth="1px"
        borderColor={colors.appGreyFive}
        paddingY="12px"
        paddingX="1.5rem"
        className="no-right-click-context-menu"
        height={'58px'}
        // @todo: use zIndex from theme, why popover not working @Yashaswi
        zIndex={9} // elevate header above grid
      >
        {BreadcrumbReplacement ??
          (!(loadingViewableParent || loadingMediaCollections) ? (
            <HStack padding={0} margin={0} spacing={0}>
              <Breadcrumb
                spacing="8px"
                separator={<ChevronRightIcon color="appSlate" />}
              >
                {Number(crumbs.length) <= 4 ? (
                  crumbs.map(x => (
                    <BreadcrumbItem key={x.href}>
                      <Icon
                        marginTop={1}
                        as={x.icon}
                        boxSize={5}
                        color="appSlate"
                        mr={2}
                        mb={1}
                      />
                      <Tooltip key={x.href} label={x.label}>
                        <BreadcrumbLink
                          as={Link}
                          to={x.href}
                          onClick={handleClick}
                          onDoubleClick={handleDoubleClick}
                          color="appSlate"
                          fontWeight="medium"
                          width="fit-content"
                          maxW={showFloatingBar ? '5em' : '8em'}
                          minWidth="unset"
                          isTruncated
                          onContextMenu={event =>
                            handleContextMenuEvent(event, x)
                          }
                          borderRadius="lg"
                          paddingX={1}
                          backgroundColor={
                            (x?.folderPath &&
                              selectedBreadcrumbFolder?.folderPath &&
                              x.folderPath ===
                                Base64.encode(
                                  selectedBreadcrumbFolder.folderPath,
                                  true,
                                )) ||
                            selectedBreadcrumbMediaCollection?.name === x.label
                              ? colors.appBlueyGrey
                              : ''
                          }
                          _focus={{
                            outline: 'none',
                          }}
                        >
                          {x.label}
                        </BreadcrumbLink>
                      </Tooltip>
                    </BreadcrumbItem>
                  ))
                ) : (
                  <>
                    <BreadcrumbItem key={crumbs[0].href}>
                      <Icon
                        as={crumbs[0].icon}
                        boxSize={5}
                        color="appSlate"
                        mr={2}
                        mb={1}
                      />
                      <BreadcrumbLink
                        as={Link}
                        to={crumbs[0].href}
                        color="appSlate"
                        fontWeight="medium"
                        minWidth="unset"
                        onClick={handleClick}
                        onDoubleClick={handleDoubleClick}
                        maxW={showFloatingBar ? '5em' : '8em'}
                        paddingX={1}
                        borderRadius="lg"
                        _focus={{
                          outline: 'none',
                        }}
                        isTruncated
                      >
                        {crumbs[0].label}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator
                      margin="0em 0.5em 0.5em 0.5em"
                      as={ChevronRightIcon}
                    />
                    <BreadcrumbItem>
                      <BreadcrumbLink fontWeight="medium">
                        <Popover trigger="click" closeOnBlur={isBlur}>
                          <PopoverTrigger>
                            <IconButton
                              aria-label="More Folders"
                              icon={<Icon as={BsThreeDots} />}
                              variant="link"
                              boxShadow="none"
                              marginX="0"
                              paddingX="0"
                              minW="min-content"
                              color="appSlate"
                            />
                          </PopoverTrigger>
                          <PopoverContent
                            maxWidth="unset"
                            width="unset"
                            maxH="200px"
                            overflowY="auto"
                            _focus={{
                              outline: 'none',
                            }}
                            ref={initialFocusRef}
                          >
                            <PopoverArrow />
                            <PopoverBody padding="0">
                              <VStack paddingY={2} paddingX={1}>
                                {crumbs
                                  .slice(1, crumbs.length - 1)
                                  .map(data => (
                                    <Box
                                      key={data.href}
                                      as={Link}
                                      to={data.href}
                                      paddingX={1}
                                      paddingY={1}
                                      width="100%"
                                      _hover={{
                                        backgroundColor: 'appGrey',
                                      }}
                                      onClick={handleClick}
                                      onDoubleClick={handleDoubleClick}
                                      onContextMenu={(
                                        event: React.MouseEvent<
                                          HTMLDivElement,
                                          MouseEvent
                                        > &
                                          React.MouseEvent<
                                            HTMLAnchorElement,
                                            MouseEvent
                                          >,
                                      ) => {
                                        void handleContextMenuEvent(event, data)
                                      }}
                                      fontWeight="normal"
                                      color="appDarkIndigo"
                                      borderRadius="lg"
                                      backgroundColor={
                                        (data?.folderPath &&
                                          selectedBreadcrumbFolder?.folderPath &&
                                          data.folderPath ===
                                            Base64.encode(
                                              selectedBreadcrumbFolder.folderPath,
                                              true,
                                            )) ||
                                        selectedBreadcrumbMediaCollection?.name ===
                                          data.label
                                          ? colors.appBlueyGrey
                                          : ''
                                      }
                                      _focus={{
                                        outline: 'none',
                                      }}
                                    >
                                      {data.label}
                                    </Box>
                                  ))}
                              </VStack>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator
                      margin="0em 0.5em 0.5em 0.5em"
                      as={ChevronRightIcon}
                    />
                    <BreadcrumbItem key={crumbs[crumbs.length - 1].href}>
                      <Icon
                        as={crumbs[crumbs.length - 1].icon}
                        boxSize={5}
                        color="appSlate"
                        mr={2}
                        mb={1}
                      />
                      <Tooltip label={crumbs[crumbs.length - 1].label}>
                        <BreadcrumbLink
                          as={Link}
                          to={crumbs[crumbs.length - 1].href}
                          color="appSlate"
                          fontWeight="medium"
                          minWidth="unset"
                          onClick={handleClick}
                          onDoubleClick={handleDoubleClick}
                          onContextMenu={event =>
                            handleContextMenuEvent(
                              event,
                              crumbs[crumbs.length - 1],
                            )
                          }
                          borderRadius="lg"
                          paddingX={1}
                          backgroundColor={
                            (crumbs[crumbs.length - 1]?.folderPath &&
                              selectedBreadcrumbFolder?.folderPath &&
                              crumbs[crumbs.length - 1].folderPath ===
                                Base64.encode(
                                  selectedBreadcrumbFolder.folderPath,
                                  true,
                                )) ||
                            selectedBreadcrumbMediaCollection?.name ===
                              crumbs[crumbs.length - 1].label
                              ? colors.appBlueyGrey
                              : ''
                          }
                          _focus={{
                            outline: 'none',
                          }}
                          maxW={showFloatingBar ? '5em' : '8em'}
                          isTruncated
                        >
                          {crumbs[crumbs.length - 1].label}
                        </BreadcrumbLink>
                      </Tooltip>
                    </BreadcrumbItem>
                  </>
                )}
              </Breadcrumb>
              {shareableAsset ? (
                <Tooltip label={t('share', 'Share')}>
                  <IconButton
                    aria-label="Share"
                    icon={
                      <Icon
                        as={MdOutlineShare}
                        boxSize={5}
                        marginBottom={1.5}
                      />
                    }
                    onClick={async event => {
                      if (shareableAsset === 'COLLECTION') {
                        dispatch([
                          {
                            type: MediaLibraryActions.SELECT_MEDIA_COLLECTION,
                            payload: {
                              mediaCollection: (
                                viewableParent as ViewableParentSuccessResponse
                              )
                                .mediaCollection as MediaLibrarySearchCollectionResponse,
                              event,
                            },
                          },
                          {
                            type: MediaLibraryActions.SET_SELECTED_BREADCRUMB_MEDIA_COLLECTION,
                            payload: {
                              mediaCollection: (
                                viewableParent as ViewableParentSuccessResponse
                              )
                                .mediaCollection as MediaLibrarySearchCollectionResponse,
                            },
                          },
                          {
                            type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                          },
                        ])
                        onOpenShareMediaCollectionModal()
                      } else if (shareableAsset === 'FOLDER') {
                        void shareFolderFunction(event)
                      }
                    }}
                    variant="linkTwo"
                  />
                </Tooltip>
              ) : null}
            </HStack>
          ) : (
            <Box w="50%" />
          ))}
        {(viewableParent as ViewableParentSuccessResponse).asset ||
        (viewableParent as ViewableParentSuccessResponse).mediaCollection ? (
          <HStack gap={'16px'}>
            {hideSortAndOtherOptions || showFloatingBar ? null : (
              <HStack spacing={'9px'}>
                <Tooltip label={t('refresh', 'Refresh')}>
                  <IconButton
                    aria-label="Refresh"
                    icon={<Icon as={MdOutlineRefresh} boxSize="1.5rem" />}
                    onClick={async () => {
                      dispatch([
                        {
                          type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                        },
                        {
                          type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                        },
                      ])
                      await refetchFolderPermission()
                      await refetchViewableParent()
                      if (!isPublicLink) {
                        await refetch()
                        await refetchDrawerFolders()
                      } else {
                        if (mediaCollectionRoute) {
                          await refetchMediaCollections()
                        } else {
                          await refetchFiles()
                          await refetchFolders()
                        }
                      }
                    }}
                    variant="linkTwo"
                  />
                </Tooltip>
                {isPublicLink ||
                (!currentMediaCollection && mediaCollectionRoute) ? (
                  <Select
                    value={sortParam}
                    sx={{
                      padding: '6px 8px',
                      height: '33px',
                      borderRadius: '4px',
                      border: '1px solid #e1e4eb',
                      backgroundColor: '#fff',
                      color: '#4a5568 !important',
                      fontWeight: 'normal',
                      fontSize: '14px',
                      lineHeight: '1.43',
                      width: 'fit-content',
                      minWidth: 0,
                      paddingRight: '30px',
                      maxWidth: '100%',
                    }}
                    iconColor="#4a5568 !important"
                    onChange={e => {
                      dispatch({
                        type: MediaLibraryActions.SET_SORT_PARAM,
                        payload: e.target.value as MLSortParams,
                      })
                    }}
                  >
                    <option value={MLSortParams.CREATED}>
                      Sort: Creation Date
                    </option>
                    <option value={MLSortParams.NAME}>Sort: Name</option>
                    ...
                    {!isPublicLink && (
                      <option value={MLSortParams.RELEVANCE}>
                        Sort: Relevance
                      </option>
                    )}
                  </Select>
                ) : (
                  <Select
                    sx={{
                      padding: '6px 8px',
                      height: '33px',
                      width: 'fit-content',
                      minWidth: 0,
                      borderRadius: '4px',
                      border: '1px solid #e1e4eb',
                      backgroundColor: '#fff',
                      color: '#4a5568',
                      fontWeight: 'normal',
                      fontSize: '14px',
                      lineHeight: '1.43',
                      maxWidth: '100%',
                      paddingRight: '30px',
                    }}
                    value={sortParam}
                    iconColor="#4a5568 !important"
                    onChange={e => {
                      dispatch({
                        type: MediaLibraryActions.SET_SORT_PARAM,
                        payload: e.target.value as MLSortParams,
                      })
                    }}
                    onClick={() => {}}
                  >
                    <option value={MLSortParams.CREATED}>
                      Sort: Creation Date
                    </option>
                    <option value={MLSortParams.UPDATED}>
                      Sort: Last Updated Date
                    </option>
                    <option value={MLSortParams.NAME}>Sort: Name</option>
                    <option value={MLSortParams.HEIGHT}>
                      Sort: Image Height
                    </option>
                    <option value={MLSortParams.WIDTH}>
                      Sort: Image Width
                    </option>
                    <option value={MLSortParams.SIZE}>Sort: File Size</option>
                    <option value={MLSortParams.RELEVANCE}>
                      Sort: Relevance
                    </option>
                  </Select>
                )}
                {sortDirection === MLSortDirection.DESC ? (
                  <Tooltip
                    label={t('sort_ascending', 'Sort in ascending order')}
                  >
                    <IconButton
                      aria-label="Sort descending"
                      variant="linkTwo"
                      icon={<Icon as={HiSortDescending} boxSize={6} />}
                      onClick={() => {
                        dispatch({
                          type: MediaLibraryActions.SET_SORT_DIRECTION,
                          payload: MLSortDirection.ASC,
                        })
                      }}
                    />
                  </Tooltip>
                ) : null}
                {sortDirection === MLSortDirection.ASC ? (
                  <Tooltip
                    label={t('sort_descending', 'Sort in descending order')}
                  >
                    <IconButton
                      aria-label="Sort Ascending"
                      variant="linkTwo"
                      icon={<Icon as={HiSortAscending} boxSize={6} />}
                      onClick={() => {
                        dispatch({
                          type: MediaLibraryActions.SET_SORT_DIRECTION,
                          payload: MLSortDirection.DESC,
                        })
                      }}
                    />
                  </Tooltip>
                ) : null}
                {!isPublicLink ? <VerticalDivider /> : null}
              </HStack>
            )}

            {showFloatingBar && (
              <HStack gap="9px">
                <IconButton
                  padding="0px"
                  margin="0px"
                  minWidth="0px"
                  onClick={() => {
                    dispatch([
                      {
                        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                      },
                      {
                        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                      },
                    ])
                  }}
                  aria-label="Close"
                  icon={<Icon as={IoMdClose} boxSize="1.5rem" />}
                  variant="linkTwo"
                />
                <Text
                  margin="0px"
                  color="#4a5568 !important"
                  fontWeight="normal"
                  fontSize="14px"
                >
                  {`${
                    selectedFiles.length +
                    selectedFolders.length +
                    selectedMediaCollections.length
                  } `}
                  <Text as="span">selected</Text>
                </Text>

                <VerticalDivider />
                <Checkbox
                  sx={{
                    '.chakra-checkbox__control': {
                      _checked: {
                        bg: '#4a5568',
                        color: 'white',
                        borderColor: '#a0aec0',
                      },
                      _indeterminate: {
                        bg: '#4a5568',
                        color: 'white',
                        borderColor: '#a0aec0',
                      },
                      _hover: {
                        bg: '#4a5568',
                        color: 'white',
                        borderColor: '#a0aec0',
                      },
                      _active: {
                         bg: '#4a5568',
                        color: 'white',
                        borderColor: '#a0aec0',
                      }
                    },
                    '.chakra-checkbox__label': {
                      fontWeight: 'normal',
                      fontSize: '14px',
                      color: '#4a5568',
                    },
                  }}
                  size="md"
                  colorScheme={'#4a5568'}
                  isChecked={
                    folderPath === '/' &&
                    !currentMediaCollection &&
                    mediaCollectionRoute
                      ? selectedMediaCollections.length !== 0 &&
                        selectedMediaCollections.length ===
                          mediaCollectionList.length
                      : [...state.selectedFiles, ...state.selectedFolders]
                          .length !== 0 &&
                        [...state.selectedFiles, ...state.selectedFolders]
                          .length ===
                          [...state.fileList, ...state.folderList].length
                  }
                  isIndeterminate={
                    folderPath === '/' &&
                    !currentMediaCollection &&
                    mediaCollectionRoute
                      ? selectedMediaCollections.length > 0 &&
                        selectedMediaCollections.length !==
                          mediaCollectionList.length
                      : [...state.selectedFiles, ...state.selectedFolders]
                          .length > 0 &&
                        [...state.selectedFiles, ...state.selectedFolders]
                          .length !==
                          [...state.fileList, ...state.folderList].length
                  }
                  onChange={e => {
                    if (!e.target.checked) {
                      dispatch([
                        {
                          type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                        },
                        {
                          type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                        },
                        {
                          type: MediaLibraryActions.SET_IS_ALL_SELECTED_FALSE,
                        },
                      ])
                    } else {
                      dispatch({
                        type: MediaLibraryActions.SELECT_ALL,
                        payload: url,
                      })
                    }
                  }}
                >
                  Select All
                </Checkbox>

                <VerticalDivider />
                {contextMenuItemList
                  .filter(x => x.viewConditions && x.name === 'Download')
                  .map((downloadItem, ind) => (
                    <Menu
                      menuButton={
                        <Box minWidth="unset" minHeight="unset" height="17.5px">
                          <Tooltip label="Download">
                            <IconButton
                              padding="0px"
                              margin="0px"
                              minWidth="0px"
                              aria-label="Download"
                              key={ind}
                              icon={<Icon as={downloadItem.icon} boxSize={5} />}
                              variant="linkTwo"
                              color="#a0aec0"
                            />
                          </Tooltip>
                        </Box>
                      }
                      submenuCloseDelay={0}
                      submenuOpenDelay={0}
                      menuStyle={{
                        padding: '4px 0px',
                        borderRadius: 8,
                        boxShadow: 'none',
                        border: `1px solid ${colors.appLightGreyTwo}`,
                        minWidth: 300,
                      }}
                    >
                      {downloadItem?.subMenu?.items
                        ?.filter(item => item.viewConditions)
                        .map((item, itemInd) => {
                          if (item.name === 'divider') {
                            return (
                              <MenuDivider
                                key={`submenu-divider-${ind}-${itemInd}`}
                              />
                            )
                          }
                          return (
                            <MenuItemWithTooltip
                              item={item}
                              ind={ind}
                              itemInd={itemInd}
                            />
                          )
                        })}
                    </Menu>
                  ))}

                {contextMenuItemList
                  .filter(x => x.viewConditions && x.name === 'Organize')[0]
                  ?.subMenu?.items?.filter(
                    x =>
                      x.viewConditions &&
                      ['Move', 'Copy'].includes(x.name) &&
                      !(x.optionDisabledInfo
                        ? x.optionDisabledInfo().isDisabled
                        : false),
                  )
                  .map((item, ind) => (
                    <Tooltip label={`${item.name} to`}>
                      <IconButton
                        padding="0px"
                        margin="0px"
                        minWidth="0px"
                        aria-label={item.name}
                        icon={<Icon as={item.icon} boxSize={5} />}
                        variant="linkTwo"
                        color="#a0aec0"
                        onClick={item.trigger}
                      />
                    </Tooltip>
                  ))}

                {contextMenuItemList
                  .filter(
                    x =>
                      x.viewConditions &&
                      ['Delete', 'Delete version', 'Restore version'].includes(
                        x.name,
                      ) &&
                      !(x.optionDisabledInfo
                        ? x.optionDisabledInfo().isDisabled
                        : false),
                  )
                  .map(item => (
                    <Tooltip label={item.name}>
                      <IconButton
                        padding="0px"
                        margin="0px"
                        minWidth="0px"
                        aria-label={item.name}
                        icon={<Icon as={item.icon} boxSize={5} />}
                        variant="linkTwo"
                        color="#a0aec0"
                        colorScheme={
                          item.name === 'Restore version'
                            ? undefined
                            : colors.appBrick
                        }
                        onClick={item.trigger}
                      />
                    </Tooltip>
                  ))}

                {!isPublicLink &&
                  contextMenuItemList.filter(x => x.viewConditions).length >
                    0 && (
                    <>
                      <Menu
                        menuButton={
                          <Box
                            minWidth="unset"
                            minHeight="unset"
                            height="17.5px"
                          >
                            <Tooltip
                              label={t('more_options', 'More Options')}
                              shouldWrapChildren
                            >
                              <IconButton
                                padding="0px"
                                margin="0px"
                                minWidth="0px"
                                aria-label="More Options"
                                icon={
                                  <Icon as={BsThreeDotsVertical} boxSize={5} />
                                }
                                variant="linkTwo"
                                color="#a0aec0"
                              />
                            </Tooltip>
                          </Box>
                        }
                        submenuCloseDelay={0}
                        submenuOpenDelay={0}
                        menuStyle={{
                          padding: '4px 0px',
                          borderRadius: 8,
                          boxShadow: 'none',
                          border: `1px solid ${colors.appLightGreyTwo}`,
                          minWidth: 200,
                        }}
                        onMouseLeave={() => {
                          Object.values(refMap).forEach(ref =>
                            ref?.current?.closeMenu(),
                          )
                        }}
                      >
                        {contextMenuItemList
                          .filter(x => x.viewConditions)
                          .map((x, ind) => {
                            if (x.subMenu) {
                              const filteredItems = x.subMenu.items?.filter(
                                item => item.viewConditions,
                              )

                              if (filteredItems?.length === 1) {
                                return (
                                  <MenuItemWithTooltip
                                    item={filteredItems[0]}
                                    ind={ind}
                                    itemInd={0}
                                  />
                                )
                              }
                              return (
                                <SubMenu
                                  position="anchor"
                                  menuStyle={{
                                    padding: 4,
                                    borderRadius: 8,
                                    boxShadow: 'none',
                                    border: `1px solid ${colors.appLightGreyTwo}`,
                                    minWidth: x.subMenu.minWidth || undefined,
                                  }}
                                  instanceRef={x.reference}
                                  label={x.element?.({})}
                                  gap={0}
                                  key={`submenu-${ind}`}
                                >
                                  {filteredItems.map((item, itemInd) => {
                                    if (item.name === 'divider') {
                                      return (
                                        <MenuDivider
                                          key={`submenu-divider-${ind}-${itemInd}`}
                                        />
                                      )
                                    }
                                    return (
                                      <MenuItemWithTooltip
                                        item={item}
                                        ind={ind}
                                        itemInd={itemInd}
                                      />
                                    )
                                  })}
                                </SubMenu>
                              )
                            } else if (x.name === 'divider') {
                              return <MenuDivider key={`divider-${ind}`} />
                            } else {
                              return <MenuItemWithTooltip item={x} ind={ind} />
                            }
                          })}
                      </Menu>
                      <VerticalDivider />
                    </>
                  )}

                {isPublicLink &&
                  contextMenuItemList
                    .filter(
                      x =>
                        x.viewConditions &&
                        x.name !== 'Download' &&
                        !x.subMenu &&
                        !(x.optionDisabledInfo
                          ? x.optionDisabledInfo().isDisabled
                          : false),
                    )
                    .map(item => (
                      <Tooltip label={item.name}>
                        <IconButton
                          padding="0px"
                          margin="0px"
                          minWidth="0px"
                          aria-label={item.name}
                          icon={<Icon as={item.icon} boxSize={5} />}
                          variant="linkTwo"
                          color="#a0aec0"
                          onClick={item.trigger}
                        />
                      </Tooltip>
                    ))}
              </HStack>
            )}
            {!isPublicLink && (
              <HStack gap={'16px'}>
                <PermissionGuard
                  permission={
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                  }
                >
                  {(hasPermission: boolean) =>
                    hasPermission && !(url === '/search') ? (
                      !currentMediaCollection && mediaCollectionRoute ? (
                        <Button
                          height="33px"
                          leftIcon={<AddIcon />}
                          onClick={onOpenCreateMediaCollectionModal}
                          colorScheme="appBlue"
                          className="create-collection-button"
                        >
                          {t('new', 'New')}
                        </Button>
                      ) : (
                        <NewButton />
                      )
                    ) : (
                      <></>
                    )
                  }
                </PermissionGuard>
                {window.isMediaLibraryWidget ? (
                  <Tooltip
                    label={
                      mlWidgetInsertButtonDisable
                        ? mlWidgetInsertButtonDisableTooltip || ''
                        : ''
                    }
                  >
                    <Box>
                      <Button
                        display={mlWidgetInsertButtonHidden ? 'none' : 'block'}
                        colorScheme="appBlue"
                        height="33px"
                        leftIcon={<AiOutlineUpload />}
                        variant="outline"
                        isDisabled={
                          state.selectedFiles.length === 0 ||
                          mlWidgetInsertButtonDisable
                        }
                        onClick={e => {
                          e.stopPropagation()
                          if (
                            window.self !== window.top &&
                            window.widgetHost !== 'uxpHost'
                          ) {
                            window.parent.postMessage(
                              {
                                eventType: 'INSERT',
                                data: JSON.parse(
                                  JSON.stringify(state.selectedFiles),
                                ),
                              },
                              window.widgetHost ?? document.referrer,
                            )
                          } else if (
                            window.widgetHost === 'uxpHost' &&
                            window.uxpHost
                          ) {
                            window.uxpHost.postMessage(
                              {
                                eventType: 'INSERT',
                                data: JSON.parse(
                                  JSON.stringify(state.selectedFiles),
                                ),
                              },
                              window.widgetHost ?? document.referrer,
                            )
                          } else {
                            console.log(
                              '[DEBUG]',
                              'Simulating response to parent',
                              {
                                eventType: 'INSERT',
                                data: state.selectedFiles,
                                widgetHost:
                                  window.widgetHost ?? document.referrer,
                              },
                            )
                          }
                        }}
                      >
                        {t('insert', 'Insert')}
                      </Button>
                    </Box>
                  </Tooltip>
                ) : null}
                <PreviewToggleButton />
              </HStack>
            )}
          </HStack>
        ) : null}
      </HStack>
      <CreateMediaCollection
        isOpen={isOpenCreateMediaCollectionModal}
        onClose={onCloseCreateMediaCollectionModal}
      />
      <ShareAssetModal
        isOpen={isOpenShareAssetModal}
        onClose={onCloseShareAssetModal}
        assetId={shareFolderResponse?.folderId as string}
        assetName={shareFolderResponse?.name as string}
        assetType={shareFolderResponse?.type as string}
      />
      <ShareMediaCollectionModal
        isOpen={isOpenShareMediaCollectionModal}
        onClose={onCloseShareMediaCollectionModal}
        assetId={
          (viewableParent as ViewableParentSuccessResponse)?.mediaCollection
            ?.mediaCollectionId as string
        }
        assetName={
          (viewableParent as ViewableParentSuccessResponse)?.mediaCollection
            ?.name as string
        }
      />
      {isOpenSaveSearchModal && (
        <SaveSearchModal
          isOpen={isOpenSaveSearchModal}
          onClose={onCloseSaveSearchModal}
        />
      )}
    </>
  )
}

export default HeaderToolbar
